import { numberToLocalString } from "@sembark-travel/number-utils"
import {
	Alert,
	Box,
	Icons,
	Container,
	Inline,
	Heading,
	Dropdown,
	Stack,
	Spinner,
	Button,
	Popover,
	joinAttributes,
	RelativeTime,
	Divider,
	Time,
	Text,
} from "@sembark-travel/ui/base"
import { queryToSearch, Link, Breadcrumbs } from "@sembark-travel/ui/router"
import { useXHR } from "@sembark-travel/xhr"
import pluralize from "pluralize"
import useSWR from "swr"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import { generatePath } from "../router-utils"
import { EditTripDestinationInDialog } from "./EditItem"
import { TTripDestination } from "./store"
import { TMedia } from "./../Media"
import { PhoneNumber, PhoneNumbersForm } from "../Contacts"
import { parseDate } from "@sembark-travel/datetime-utils"
import {
	checkFileSize,
	checkFileTypes,
	Form,
	FormSpy,
	GetFieldValue,
	ImageCropInputField,
	RadioInputField,
	SelectInputField,
	SubmissionError,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui/form"

import { mixed as mixedValidator, object as objectValidator } from "yup"
import { useState } from "react"

const defaultAccept = ["image/png", "image/jpeg"]
type TImageType = {
	file: File | null
	size_type: string
	overlay_type: string
}

export function TripDestinationItemDetails({
	tripDestinationId,
}: {
	tripDestinationId: string | number
}) {
	const xhr = useXHR()
	const { hasPermission } = useCheckPermissions()
	const { data, error, mutate } = useSWR<TTripDestination>(
		`/trip-destinations/${tripDestinationId}`,
		() =>
			xhr
				.get(`/trip-destinations/${tripDestinationId}`, {
					params: {
						include:
							"trips_count,in_progress_trips_count,converted_trips_count",
					},
				})
				.then((resp) => resp.data.data)
	)
	if (!data && error) {
		return <Alert status="error">{error.message}</Alert>
	}
	if (!data) {
		return <Spinner padding="4" alignCenter />
	}
	const {
		id,
		name,
		short_name,
		trips_count = 0,
		banner_latest_media,
		reviews_latest_media,
		currency,
		phone_numbers,
		addresses,
		min_child_age,
		max_child_age,
		hotel_checkin_at,
		hotel_checkout_at,
		created_at,
		created_by,
		updated_at,
		updated_by,
		deleted_at,
		deleted_by,
	} = data
	const canUpdate = hasPermission(PERMISSIONS.ADD_TRIP_DESTINATIONS)
	return (
		<Box key={id}>
			<Breadcrumbs
				title="Details"
				items={[
					[generatePath("/org/trip-destinations"), "Trip Destinations"],
					["", name],
				]}
				actions={
					canUpdate ? (
						<Box>
							<EditTripDestinationInDialog
								destination={data}
								onSuccess={() => mutate()}
							>
								{({ onEdit }) => (
									<Dropdown alignRight>
										<Dropdown.ToggleButton level="tertiary">
											<Icons.DotsVertical />
										</Dropdown.ToggleButton>
										<Dropdown.Menu>
											<Dropdown.MenuItem onClick={() => onEdit()}>
												<Icons.Pencil /> Edit Details
											</Dropdown.MenuItem>
										</Dropdown.Menu>
									</Dropdown>
								)}
							</EditTripDestinationInDialog>
						</Box>
					) : null
				}
			/>
			<Stack gap={"6"}>
				<Container paddingY="6" bgColor="default" borderBottomWidth="1">
					<Inline collapseBelow="md" justifyContent="between" gap="6">
						<Stack gap="4">
							<Stack gap="2">
								<Heading as="h2">
									{name} ({short_name})
								</Heading>
								<Box>
									{joinAttributes(
										<Text as="span" title="Operational Currency">
											{currency}
										</Text>,
										<Link
											fontWeight="semibold"
											to={
												generatePath("/trips") +
												queryToSearch({
													destinations: [`${id}_${name}`],
													status: "all",
												})
											}
										>
											{numberToLocalString(trips_count)}{" "}
											{pluralize("Trip", trips_count)}
										</Link>
									)}
								</Box>
							</Stack>
							<Stack gap="2">
								<Inline gap="4">
									<Stack gap="2" alignItems="start">
										<Inline gap="2" alignItems="center">
											<Text>Address(es):</Text>
											{addresses?.length ? (
												<Inline gap="4">
													{addresses.map((a) => (
														<Text
															key={a.id}
															title={
																a.is_billing
																	? "Billing Address"
																	: "Contact Address"
															}
														>
															{a.is_billing ? (
																<Icons.BankNotes />
															) : (
																<Icons.LifeBouy />
															)}{" "}
															{a.name}
														</Text>
													))}
												</Inline>
											) : (
												<Text color="muted">Primary Address</Text>
											)}
										</Inline>
										<Inline gap="2" alignItems="center">
											<Text>Child Age Range:</Text>
											<Text>
												{min_child_age} - {max_child_age} yrs
											</Text>
										</Inline>
									</Stack>

									<Stack
										gap="2"
										alignItems="start"
										borderLeftWidth="1"
										paddingLeft="4"
									>
										<Inline gap="2" alignItems="center">
											<Text>Default Hotel Checkin:</Text>
											<Text>
												<Time
													value={parseDate(hotel_checkin_at, "HH:mm:ss")}
													format="HH:mm"
												/>{" "}
												hrs
											</Text>
										</Inline>
										<Inline gap="2" alignItems="center">
											<Text>Default Hotel Checkout:</Text>
											<Text>
												<Time
													value={parseDate(hotel_checkout_at, "HH:mm:ss")}
													format="HH:mm"
												/>{" "}
												hrs
											</Text>
										</Inline>
									</Stack>
								</Inline>

								<Inline gap="4" alignItems="center">
									<Inline gap="2" alignItems="center">
										<Text>Contact Numbers:</Text>
										{phone_numbers?.length ? (
											<PhoneNumber value={phone_numbers} />
										) : (
											<Text color="muted">Not Set</Text>
										)}
									</Inline>
									{canUpdate ? (
										<Popover
											trigger="click"
											content={({ hide }) => (
												<Box padding="4">
													<Stack gap="4">
														<Text color="muted" maxWidth="sm">
															Add phone numbers that can be used in sharing of
															packages and bookings for this destination.
														</Text>
														<PhoneNumbersForm
															phoneNumbers={phone_numbers}
															onSubmit={async (values) => {
																await xhr.patch(
																	`/trip-destinations/${id}/phone-numbers`,
																	{
																		phone_numbers: values.phone_numbers,
																	}
																)
																mutate()
																hide()
															}}
														/>
														<Text color="muted" maxWidth="sm">
															<Icons.Info opacity="50" /> Leaving empty will use
															contact details from your organization.
														</Text>
													</Stack>
												</Box>
											)}
										>
											<Button size="sm" level="tertiary">
												<Icons.Pencil />
											</Button>
										</Popover>
									) : null}
								</Inline>
								<Divider marginY="0" />
								<Inline gap="4" flexWrap="wrap">
									<Stack>
										<Text color="muted" fontSize="sm">
											Created By
										</Text>
										<Text>
											{created_by?.name},{" "}
											<RelativeTime timestamp={created_at} />
										</Text>
									</Stack>
									{updated_at !== created_at && updated_by ? (
										<Stack>
											<Text color="muted" fontSize="sm">
												Last Updated By
											</Text>
											<Text>
												{updated_by?.name},{" "}
												<RelativeTime timestamp={updated_at} />
											</Text>
										</Stack>
									) : null}
									{deleted_at ? (
										<Stack color="warning">
											<Text fontSize="sm">Disabled By</Text>
											<Text>
												{deleted_by?.name},{" "}
												<RelativeTime timestamp={deleted_at} />
											</Text>
										</Stack>
									) : null}
								</Inline>
							</Stack>
						</Stack>
					</Inline>
				</Container>
				<DestinationSection
					media={banner_latest_media}
					canEdit={hasPermission(PERMISSIONS.MANAGE_TRIP_OWNERS)}
					onSuccess={mutate}
					destinationId={id}
				/>
				<BrandingSection
					media={reviews_latest_media}
					canEdit={hasPermission(PERMISSIONS.MANAGE_TRIP_OWNERS)}
					onSubmit={async (data) => {
						await xhr.patch(`/trip-destinations/${id}/image`, data)
						mutate()
					}}
				/>
			</Stack>
		</Box>
	)
}

function DestinationSection({
	media,
	canEdit,
	onSuccess,
	destinationId,
}: {
	media?: TMedia | null
	canEdit: boolean
	onSuccess: () => void
	destinationId: number
}) {
	const xhr = useXHR()
	const [imageFormState, setImageFormState] = useState<boolean>(false)
	const [overlayForm, setOverlayForm] = useState<boolean>(false)

	return (
		<Container
			padding="4"
			bgColor="default"
			borderBottomWidth="1"
			overflow="hidden"
		>
			{overlayForm && media ? (
				<OverlayForm
					media={media}
					onCancel={() => {
						setOverlayForm(false)
						setImageFormState(false)
					}}
					onSubmit={async (data) => {
						await xhr.patch(`/trip-destination-images/${media.id}`, data)
						onSuccess()
						setImageFormState(false)
						setOverlayForm(false)
					}}
				/>
			) : imageFormState ? (
				<NewImageForm
					onSubmit={async (data) => {
						await xhr.patch(`/trip-destinations/${destinationId}/image`, data)
						onSuccess()
						setImageFormState(false)
						setOverlayForm(false)
					}}
					onCancel={() => {
						setImageFormState(false)
						setOverlayForm(false)
					}}
				/>
			) : (
				<Inline gap={"4"} justifyContent={"between"} collapseBelow="md">
					<Stack gap="4" style={{ minWidth: "20rem" }}>
						<Box>
							<Heading as="h2">Destination Image</Heading>
							<Text color={"muted"}>
								This image appears on first page of your PDF
							</Text>
						</Box>

						<Stack gap={"4"}>
							{!media ? null : (
								<>
									<Stack gap={"2"} maxWidth={"sm"}>
										<Text fontWeight="semibold">Selected image type</Text>
										<Box
											borderRadius="md"
											borderWidth={"1"}
											paddingY="2"
											paddingX={"4"}
											bgColor={"primary"}
											textTransform="capitalize"
										>
											{media.custom_properties?.image_size_type} Image
										</Box>
										<Text color={"muted"} fontSize={"sm"}>
											{media.custom_properties?.image_size_type === "portrait"
												? "The image will cover the entire A4 size PDF page (first page)."
												: "The image will cover 1/3 of A4 size PDF page (first page)."}
										</Text>
									</Stack>

									{media.custom_properties?.image_size_type === "portrait" ? (
										<Stack gap={"2"} maxWidth={"sm"}>
											<Inline gap={"2"}>
												<Text fontWeight="semibold">Selected overlay</Text>
												{canEdit ? (
													<Button
														size="sm"
														level="tertiary"
														onClick={() => setOverlayForm(true)}
													>
														<Icons.Pencil />
													</Button>
												) : null}
											</Inline>

											<Box
												borderRadius="md"
												borderWidth={"1"}
												paddingY="2"
												paddingX={"4"}
												bgColor={"primary"}
												textTransform="capitalize"
											>
												{media.custom_properties?.overlay_type || "none"}{" "}
												Overlay
											</Box>
											<Text color={"muted"} fontSize={"sm"}>
												{media.custom_properties?.overlay_type !== "none"
													? "This section appears on bottom of the first page of your PDF and displays trip details along with sales contact information."
													: "Display the full page image without any overlay."}
											</Text>
										</Stack>
									) : null}
								</>
							)}
							{canEdit ? (
								<Inline paddingTop={"2"} gap={"2"}>
									<Button onClick={() => setImageFormState(true)}>
										{!media ? "Add Image" : "Update Image"}
									</Button>
									{media?.url ? (
										<Button
											onClick={async () => {
												if (
													window.confirm(
														`Are you sure you want to remove this image`
													)
												) {
													const data = new FormData()
													data.append("file_collection_name", "banner")
													await xhr.patch(
														`/trip-destinations/${destinationId}/image`,
														data
													)
													onSuccess()
												}
											}}
											status="warning"
										>
											Remove
										</Button>
									) : null}
								</Inline>
							) : null}
						</Stack>
					</Stack>
					<Box maxWidth={"2xl"}>
						{media ? (
							<ImagePreview
								image_url={media.url}
								width={
									media.custom_properties?.image_size_type === "portrait"
										? "264px"
										: "auto"
								}
								height={
									media.custom_properties?.image_size_type === "portrait"
										? "394px"
										: "auto"
								}
								overlay={
									media.custom_properties?.image_size_type === "portrait"
										? true
										: false
								}
								overlay_type={media.custom_properties?.overlay_type ?? "none"}
								media={media}
							/>
						) : (
							<ImagePreview />
						)}
					</Box>
				</Inline>
			)}
		</Container>
	)
}

function NewImageForm({
	allowedTypes = defaultAccept,
	onSubmit,
	onCancel,
}: {
	allowedTypes?: typeof defaultAccept
	onSubmit: (data: FormData) => Promise<unknown>
	onCancel?: () => void
}) {
	const initialValue = {
		file: null,
		size_type: "landscape",
		overlay_type: "none",
	} as TImageType
	return (
		<Form<TImageType>
			initialValues={initialValue}
			validate={validateFormValues(
				objectValidator().shape({
					file: mixedValidator()
						.required("Please select a file")
						.test(
							"type-is-correct",
							`Please select an ${allowedTypes.join(", ")} image file`,
							checkFileTypes(allowedTypes)
						)
						.test(
							"size-not-big",
							`Please select a file that is less then 1000kB in size`,
							checkFileSize(1000)
						),
				})
			)}
			onSubmit={withServerErrors(async (values) => {
				const data = new FormData()
				data.append("file", values.file || "")
				data.append("size_type", values.size_type)
				data.append("file_collection_name", "banner")
				if (values.size_type === "portrait") {
					data.append("overlay_type", values?.overlay_type)
				}
				await onSubmit(data)
			})}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit, form }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Inline gap={"4"} justifyContent={"between"} collapseBelow="md">
						<Stack gap="4" maxWidth={"3xl"}>
							<Box>
								<Heading as="h2">Destination Image</Heading>
								<Text color={"muted"}>
									This image appears on first page of your PDF
								</Text>
							</Box>

							<Stack gap={"4"} maxWidth={"full"}>
								<GetFieldValue<string> name={"size_type"}>
									{({ value: size }) => (
										<>
											<SelectInputField
												name="size_type"
												label="Select image type"
												style={{ maxWidth: "370px" }}
												help={
													size === "portrait"
														? "The image will cover the entire A4 size PDF page (first page)."
														: "The image will cover approximately one-third of the A4 page height (first page)."
												}
												onChange={(values) => {
													form.batch(() => {
														form.change("file", null)
														form.change("size_type", values.currentTarget.value)
													})
												}}
											>
												<option value="landscape">Landscape Image</option>
												<option value="portrait">Portrait Image</option>
											</SelectInputField>
											<ImageCropInputField
												label="Select an image"
												crop={
													size === "portrait"
														? false
														: {
																aspect: 2 / 1,
																minHeight: 100,
															}
												}
												name={"file"}
												accept={allowedTypes.join(",")}
												help={
													size === "portrait"
														? "Image must be 1200x1800 JPEG/PNG file with less than 1 MB in size."
														: "Image must be 1200x600 JPEG/PNG file with less than 1 MB in size."
												}
											/>
											{size === "portrait" ? <OverlayTheme /> : null}
										</>
									)}
								</GetFieldValue>

								<Inline gap="4">
									<Button type="submit" disabled={submitting}>
										{submitting ? "Uploading..." : "Upload"}
									</Button>
									<Button
										disabled={submitting}
										onClick={() => {
											onCancel?.()
										}}
										level="tertiary"
									>
										Cancel
									</Button>
								</Inline>
							</Stack>

							<SubmissionError />
						</Stack>

						<Stack paddingTop={"2"} maxWidth={"2xl"}>
							<FormSpy<typeof initialValue> subscription={{ values: true }}>
								{({ values }) =>
									values.file ? (
										<ImagePreview
											image_url={URL.createObjectURL(values.file)}
											// the ratio landscape image is 2/1 image
											// in case of portrait image, the ratio is 2/3
											width={
												values.size_type === "portrait" ? "264px" : "42rem"
											}
											height={
												values.size_type === "portrait" ? "394px" : "21rem"
											}
											overlay={values.size_type === "portrait" ? true : false}
											overlay_type={values.overlay_type}
											preview={values.file ? true : false}
										/>
									) : (
										<ImagePreview />
									)
								}
							</FormSpy>
						</Stack>
					</Inline>
				</form>
			)}
		</Form>
	)
}

function OverlayForm({
	media,
	onCancel,
	onSubmit,
}: {
	media: TMedia
	onCancel?: () => void
	onSubmit: (props: { overlay_type: string }) => Promise<unknown>
}) {
	return (
		<Form
			initialValues={{
				overlay_type: media.custom_properties?.overlay_type ?? "none",
			}}
			onSubmit={withServerErrors(async (values) => {
				await onSubmit(values)
			})}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Inline gap={"4"} justifyContent={"between"} collapseBelow="md">
						<Stack gap="4" maxWidth={"3xl"}>
							<Box>
								<Heading as="h2">Destination Image</Heading>
								<Text color={"muted"}>
									This image appears on first page of your PDF
								</Text>
							</Box>
							<OverlayTheme />
							<Inline gap="4">
								<Button type="submit" disabled={submitting}>
									{submitting ? "Updating..." : "Update"}
								</Button>
								<Button
									disabled={submitting}
									onClick={() => {
										onCancel?.()
									}}
									level="tertiary"
								>
									Cancel
								</Button>
							</Inline>
						</Stack>
						<Box maxWidth={"2xl"}>
							<GetFieldValue<string> name="overlay_type">
								{({ value }) => (
									<ImagePreview
										image_url={media.url}
										width={"264px"}
										height={"394px"}
										overlay={true}
										overlay_type={value}
										media={media}
									/>
								)}
							</GetFieldValue>
						</Box>
					</Inline>
				</form>
			)}
		</Form>
	)
}

function OverlayTheme() {
	return (
		<Stack gap={"2"}>
			<Box>
				<Text fontWeight={"semibold"}>Image overlay</Text>
				<Text fontSize={"sm"} color={"muted"}>
					This section appears on bottom of the first page of your PDF and
					displays trip details along <br />
					with sales contact information.
				</Text>
			</Box>
			<GetFieldValue<string> name="overlay_type">
				{({ value }) => (
					<Inline gap={"2"}>
						<Box
							paddingX={"4"}
							paddingY={"2"}
							borderWidth={"1"}
							rounded={"md"}
							bgColor={value === "dark" ? "primary" : "default"}
							as="label"
							cursor="pointer"
						>
							<RadioInputField
								name={"overlay_type"}
								value={"dark"}
								label={"Dark"}
								help={
									"Recommendation: Best with light background images for enhanced content visibility."
								}
							/>
						</Box>

						<Box
							paddingX={"4"}
							paddingY={"2"}
							borderWidth={"1"}
							rounded={"md"}
							bgColor={value === "light" ? "primary" : "default"}
							as="label"
							cursor="pointer"
						>
							<RadioInputField
								name={"overlay_type"}
								value={"light"}
								label={"Light"}
								help={
									"Recommendation: Optimal with dark background images for clear legibility."
								}
							/>
						</Box>

						<Box
							paddingX={"4"}
							paddingY={"2"}
							borderWidth={"1"}
							rounded={"md"}
							bgColor={value === "none" ? "primary" : "default"}
							as="label"
							cursor="pointer"
						>
							<RadioInputField
								name={"overlay_type"}
								value={"none"}
								label={"None"}
								help={"Display the full page image without any overlay."}
							/>
						</Box>
					</Inline>
				)}
			</GetFieldValue>
		</Stack>
	)
}

function BrandingSection({
	media,
	canEdit,
	onSubmit,
}: {
	media?: TMedia | null
	canEdit: boolean
	onSubmit: (data: FormData) => Promise<unknown>
}) {
	const [state, setState] = useState(false)
	return (
		<Container padding="4" bgColor="default" borderBottomWidth="1">
			{state ? (
				<NewBrandingImageForm
					onSubmit={async (data) => {
						await onSubmit(data)
						setState(false)
					}}
					onCancel={() => setState(false)}
				/>
			) : (
				<Inline gap={"2"} justifyContent={"between"} collapseBelow="md">
					<Stack gap="4">
						<Box>
							<Heading as="h2">Review/Branding Section</Heading>
							<Text color={"muted"}>
								This image to be displayed in the last page of your review.
							</Text>
						</Box>

						{canEdit ? (
							<Inline paddingTop={"2"} gap={"2"}>
								<Button onClick={() => setState(true)}>
									{!media ? "Add Image" : "Update Image"}
								</Button>
								{media?.url ? (
									<Button
										onClick={async () => {
											if (
												window.confirm(
													`Are you sure you want to remove this image`
												)
											) {
												const data = new FormData()
												data.append("file", "")
												data.append("file_collection_name", "reviews")
												await onSubmit(data)
											}
										}}
										status="warning"
									>
										Remove
									</Button>
								) : null}
							</Inline>
						) : null}
					</Stack>
					<ImagePreview
						image_url={media?.url}
						width="264px"
						height="370px"
						media={media}
					/>
				</Inline>
			)}
		</Container>
	)
}

function NewBrandingImageForm({
	onSubmit,
	onCancel,
}: {
	onSubmit: (data: FormData) => Promise<unknown>
	onCancel?: () => void
}) {
	const initialValue = {
		file: null,
	} as { file: File | null }

	return (
		<Form<{ file: File | null }>
			initialValues={initialValue}
			validate={validateFormValues(
				objectValidator().shape({
					file: mixedValidator()
						.required("Please select a file")
						.test(
							"type-is-correct",
							`Please select an ${defaultAccept.join(", ")} image file`,
							checkFileTypes(defaultAccept)
						)
						.test(
							"size-not-big",
							`Please select a file that is less then 1000kB in size`,
							checkFileSize(1000)
						),
				})
			)}
			onSubmit={withServerErrors(async (values) => {
				const data = new FormData()
				data.append("file", values.file || "")
				data.append("file_collection_name", "reviews")
				await onSubmit(data)
			})}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Inline gap={"4"} justifyContent={"between"} collapseBelow="md">
						<Stack gap="4" maxWidth={"3xl"}>
							<Box>
								<Heading as="h2">Review/Branding Section</Heading>
								<Text color={"muted"}>
									This image to be displayed in the last page of your review.
								</Text>
							</Box>

							<Stack gap={"4"} maxWidth={"full"}>
								<ImageCropInputField
									label="Select an image"
									crop={false}
									name={"file"}
									accept={defaultAccept.join(",")}
									help="Image must be of ratio 2/3 (eg:1200x1800). JPEG/PNG file with less than 1 MB in size."
								/>

								<Inline gap="4">
									<Button type="submit" disabled={submitting}>
										{submitting ? "Uploading..." : "Upload"}
									</Button>
									<Button
										disabled={submitting}
										onClick={() => {
											onCancel?.()
										}}
										level="tertiary"
									>
										Cancel
									</Button>
								</Inline>
							</Stack>

							<SubmissionError />
						</Stack>

						<Stack paddingTop={"2"} maxWidth={"2xl"}>
							<FormSpy<typeof initialValue> subscription={{ values: true }}>
								{({ values }) =>
									values.file ? (
										<ImagePreview
											image_url={URL.createObjectURL(values.file)}
											width="264px"
											height="370px"
											preview={true}
										/>
									) : (
										<ImagePreview />
									)
								}
							</FormSpy>
						</Stack>
					</Inline>
				</form>
			)}
		</Form>
	)
}
/**
 * In case of preview there will be no media
 * user is coming for the first time he will not have image_url
 */
function ImagePreview({
	image_url,
	height = "auto",
	width = "100%",
	overlay_type,
	overlay = false,
	preview = false,
	media,
}: {
	image_url?: string | null
	height?: string
	width?: string
	overlay_type?: string
	overlay?: boolean
	preview?: boolean
	media?: TMedia | null
}) {
	return (
		<Box>
			{image_url ? (
				<Box>
					<Box
						borderWidth="1"
						rounded="md"
						overflow="hidden"
						style={{
							width: width,
							height: height,
						}}
						position={"relative"}
					>
						<img
							src={image_url}
							style={{
								width: width,
								height: height,
								maxHeight: "400px",
							}}
							alt="Display"
						/>
						{overlay && overlay_type !== "none" ? (
							<Box
								position={"absolute"}
								borderRadius="sm"
								style={{
									backgroundColor: overlay_type === "light" ? "#fff" : "#000",
									width: "240px",
									height: "44px",
									bottom: "6%",
									left: "5%",
									color: overlay_type === "light" ? "#000" : "#fff",
								}}
							>
								<Inline justifyContent={"evenly"}>
									<Box
										paddingY={"1"}
										paddingLeft={"2"}
										paddingRight={"3"}
										borderRightWidth={"1"}
										style={{
											height: "44px",
											fontSize: "6px",
											fontWeight: "lighter",
										}}
									>
										<Text>Trip ID: 2345</Text>
										<Inline alignItems={"center"} gap={"1"}>
											Dubai • 4A,3C (4yr,3yr,2yr)
										</Inline>
										<svg
											width="100"
											height="12"
											viewBox="0 0 115 12"
											fill="none"
											stroke="currentColor"
											strokeWidth={0.3}
											xmlns="http://www.w3.org/2000/svg"
										>
											<g clipPath="url(#clip0_516_22805)">
												<path d="M106.063 9.56998H105.928V9.36984C105.928 9.181 105.776 9.02832 105.587 9.02832H104.571C104.384 9.02832 104.23 9.18097 104.23 9.36984V9.56998H104.096C104.018 9.56998 103.955 9.6333 103.955 9.71133C103.955 9.78936 104.018 9.85268 104.096 9.85268H106.063C106.141 9.85268 106.204 9.78936 106.204 9.71133C106.204 9.6333 106.141 9.56998 106.063 9.56998ZM105.646 9.56998H104.513V9.36984C104.513 9.33703 104.54 9.31104 104.571 9.31104H105.587C105.62 9.31104 105.646 9.33705 105.646 9.36984V9.56998Z" />
												<path d="M114.727 9.56998H114.592V9.36984C114.592 9.181 114.44 9.02832 114.252 9.02832H113.235C113.048 9.02832 112.895 9.18097 112.895 9.36984V9.56998H112.76C112.682 9.56998 112.619 9.6333 112.619 9.71133C112.619 9.78936 112.682 9.85268 112.76 9.85268H114.727C114.805 9.85268 114.868 9.78936 114.868 9.71133C114.868 9.6333 114.805 9.56998 114.727 9.56998ZM114.31 9.56998H113.178V9.36984C113.178 9.33703 113.204 9.31104 113.235 9.31104H114.252C114.284 9.31104 114.31 9.33705 114.31 9.36984V9.56998Z" />
												<path d="M113.744 1.32227C113.503 1.32227 113.308 1.5179 113.308 1.75875V2.17375H109.412H105.516V1.75877C105.516 1.51226 105.315 1.32229 105.08 1.32229C104.84 1.32229 104.645 1.51792 104.645 1.75877V8.60473C104.645 8.68276 104.708 8.74608 104.786 8.74608C104.864 8.74608 104.927 8.68276 104.927 8.60473V1.75877C104.927 1.67395 104.995 1.60498 105.08 1.60498C105.161 1.60498 105.234 1.67057 105.234 1.75877V8.60473C105.234 8.68276 105.297 8.74608 105.375 8.74608C105.453 8.74608 105.516 8.68276 105.516 8.60473V5.7856H113.308V8.60471C113.308 8.68274 113.371 8.74606 113.449 8.74606C113.527 8.74606 113.59 8.68274 113.59 8.60471V1.75877C113.59 1.67395 113.659 1.60498 113.744 1.60498C113.828 1.60498 113.897 1.67395 113.897 1.75877V8.60473C113.897 8.68276 113.96 8.74608 114.038 8.74608C114.116 8.74608 114.179 8.68276 114.179 8.60473V1.75877C114.18 1.51903 113.986 1.32227 113.744 1.32227ZM106.354 4.41506H105.516V3.54432H106.354V4.41506ZM107.541 5.50288H106.67V4.66495H107.541V5.50288ZM107.541 3.29441H106.67V2.45647H107.541V3.29441ZM108.661 4.41506H107.79V3.54432H108.661V4.41506ZM109.847 5.50288H108.977V4.66495H109.847V5.50288ZM109.847 3.29441H108.977V2.45647H109.847V3.29441ZM110.968 4.41506H110.097V3.54432H110.968V4.41506ZM112.154 5.50288H111.284V4.66495H112.154V5.50288ZM112.154 3.29441H111.284V2.45647H112.154V3.29441ZM113.308 4.41506H112.404V3.54432H113.308V4.41506Z" />
											</g>
											<path d="M114.754 11V10.5H113.77V11V11.5H114.754V11ZM111.801 11V10.5L109.832 10.5V11V11.5L111.801 11.5V11ZM107.863 11V10.5H105.895V11V11.5H107.863V11ZM103.926 11V10.5H101.957V11V11.5H103.926V11ZM99.9886 11V10.5H98.0198V11V11.5H99.9886V11ZM96.0511 11V10.5H94.0824V11V11.5H96.0511V11ZM92.1137 11V10.5H90.145V11V11.5L92.1137 11.5V11ZM88.1763 11V10.5H86.2076V11V11.5H88.1763V11ZM84.2389 11V10.5L82.2702 10.5V11V11.5L84.2389 11.5V11ZM80.3014 11V10.5H78.3327V11V11.5H80.3014V11ZM76.364 11V10.5L74.3953 10.5V11V11.5L76.364 11.5V11ZM72.4266 11V10.5H70.4579V11V11.5H72.4266V11ZM68.4892 11V10.5H66.5205V11V11.5L68.4892 11.5V11ZM64.5517 11V10.5H62.583V11V11.5H64.5517V11ZM60.6143 11V10.5H58.6456V11V11.5H60.6143V11ZM56.6769 11V10.5H54.7082V11V11.5H56.6769V11ZM52.7395 11V10.5H50.7708V11V11.5H52.7395V11ZM48.802 11V10.5L46.8333 10.5V11V11.5L48.802 11.5V11ZM44.8646 11V10.5H42.8959V11V11.5H44.8646V11ZM40.9272 11V10.5L38.9585 10.5V11V11.5L40.9272 11.5V11ZM36.9898 11V10.5H35.0211V11V11.5H36.9898V11ZM33.0524 11V10.5H31.0836V11V11.5H33.0524V11ZM29.1149 11V10.5H27.1462V11V11.5H29.1149V11ZM25.1775 11V10.5H23.2088V11V11.5H25.1775V11ZM21.2401 11V10.5L19.2714 10.5V11V11.5L21.2401 11.5V11ZM17.3027 11V10.5H15.3339V11V11.5H17.3027V11ZM13.3652 11V10.5L11.3965 10.5V11V11.5L13.3652 11.5V11ZM9.42781 11V10.5H7.4591V11V11.5H9.42781V11ZM5.49039 11V10.5L4.50599 10.5V11V11.5L5.49039 11.5V11Z" />
											<g clipPath="url(#clip1_516_22805)">
												<path d="M1.22454 4.60514C1.23596 4.59289 1.25019 4.5839 1.26593 4.57899C1.28168 4.57408 1.29843 4.5734 1.31468 4.57701C1.67159 4.65628 2.03866 4.65646 2.39211 4.57754C2.74556 4.49862 3.07751 4.34237 3.36646 4.11889C3.91871 3.692 4.60769 3.4997 5.29912 3.57945C5.26836 3.50483 5.25625 3.42362 5.26397 3.34362C5.27169 3.26362 5.29897 3.18754 5.34319 3.12268C5.38742 3.05782 5.44709 3.00638 5.51648 2.97329C5.58586 2.94021 5.66263 2.9266 5.73938 2.93378C5.81613 2.94095 5.8903 2.96867 5.95473 3.01427C6.01917 3.05986 6.07171 3.1218 6.1073 3.19412C6.14289 3.26644 6.16033 3.34671 6.15795 3.4272C6.15557 3.5077 6.13344 3.58571 6.09369 3.65374L7.44665 9.6808C7.45269 9.70778 7.44832 9.73591 7.4345 9.75902C7.42068 9.78213 7.39854 9.79834 7.37294 9.80407L6.94265 9.90014C6.91705 9.90584 6.89009 9.9006 6.86771 9.88558C6.84533 9.87055 6.82935 9.84696 6.82328 9.81999L6.24922 7.2627C5.90143 7.19454 5.5457 7.20109 5.20373 7.28194C4.86177 7.3628 4.54076 7.51627 4.2603 7.73298C3.94924 7.97357 3.59188 8.1418 3.21137 8.22676C2.83086 8.31172 2.4357 8.31152 2.05147 8.22617C2.03261 8.22198 2.01519 8.21217 2.00132 8.19793C1.98745 8.1837 1.97772 8.16564 1.97333 8.14596L1.19982 4.70019C1.196 4.68326 1.19627 4.66569 1.20058 4.64909C1.2049 4.63249 1.21313 4.61738 1.22454 4.60514ZM5.73349 4.96552C5.30767 4.88195 4.87117 4.91064 4.46356 5.049L4.6756 5.99357C5.08247 5.85846 5.51582 5.82436 5.9419 5.89393L5.73349 4.96552ZM4.27716 5.12187C4.08781 5.20363 3.90902 5.30912 3.74463 5.43608C3.55808 5.58027 3.35433 5.69873 3.13822 5.78864L3.34958 6.73018C3.53894 6.64843 3.71772 6.54294 3.88211 6.41597C4.06866 6.27178 4.27241 6.15332 4.48852 6.06342L4.27716 5.12187ZM2.95115 5.85848C2.86709 5.88635 2.78195 5.91042 2.69567 5.92969C2.36453 6.00364 2.02186 6.01314 1.68457 5.9577L1.89306 6.88646C2.31886 6.97002 2.75535 6.94133 3.16294 6.80298L2.95115 5.85848ZM1.42677 4.80924L1.63522 5.73783C2.06101 5.82144 2.49751 5.79277 2.9051 5.65441L2.69298 4.70944C2.28616 4.84467 1.85282 4.87883 1.42677 4.80924ZM2.88044 4.6403L3.0918 5.58184C3.28117 5.50011 3.45995 5.39462 3.62433 5.26763C3.81086 5.1234 4.01461 5.00491 4.23071 4.91496L4.01928 3.97307C3.82992 4.05482 3.65114 4.16031 3.48675 4.28728C3.30024 4.43159 3.09652 4.5502 2.88044 4.6403ZM4.20569 3.90026L4.41775 4.84494C4.82462 4.7098 5.25798 4.6757 5.68405 4.7453L5.4756 3.81671C5.04979 3.73317 4.61329 3.76188 4.20569 3.90026ZM5.65235 3.14598C5.60423 3.15673 5.56056 3.18226 5.52688 3.21936C5.49319 3.25646 5.471 3.30346 5.4631 3.35441C5.4552 3.40536 5.46196 3.45797 5.48251 3.50559C5.50306 3.55322 5.53649 3.59371 5.57857 3.62196C5.62064 3.6502 5.66947 3.66493 5.71889 3.66427C5.7683 3.66362 5.81608 3.64761 5.85617 3.61828C5.89627 3.58895 5.92688 3.5476 5.94415 3.49948C5.96141 3.45135 5.96454 3.39861 5.95315 3.34791C5.93782 3.27998 5.89754 3.22057 5.84115 3.18271C5.78476 3.14485 5.71685 3.13164 5.65235 3.14598ZM7.23077 9.62232L5.92775 3.81774C5.85557 3.85867 5.77332 3.87703 5.69054 3.87071L6.99355 9.67528L7.23077 9.62232ZM6.19982 7.04266L5.99136 6.11402C5.56554 6.03045 5.12904 6.05915 4.72143 6.1975L4.93346 7.14201C5.34036 7.007 5.77377 6.97299 6.19982 7.04266ZM4.74643 7.21209L4.53502 6.27031C4.34565 6.35204 4.16687 6.45753 4.00249 6.58452C3.81595 6.72871 3.61219 6.84717 3.39608 6.93708L3.6075 7.87886C3.79686 7.79712 3.97565 7.69163 4.14002 7.56465C4.32657 7.42049 4.53035 7.30203 4.74643 7.21209ZM3.42095 7.95109L3.20899 7.00687C3.12493 7.03473 3.03981 7.05886 2.95353 7.07813C2.6224 7.15215 2.27972 7.16171 1.94242 7.10633L2.15117 8.03625C2.57705 8.11929 3.01355 8.09001 3.42095 7.95109Z" />
											</g>
											<g clipPath="url(#clip2_516_22805)">
												<path d="M12.6025 5.38228C12.593 5.36833 12.5802 5.35731 12.5653 5.35021C12.5504 5.34311 12.5339 5.34015 12.5173 5.34162C12.1514 5.37364 11.7872 5.32421 11.4484 5.19652C11.1096 5.06882 10.8037 4.86571 10.5504 4.60034C10.0665 4.0934 9.41176 3.80552 8.71395 3.79283C8.75562 3.72142 8.77978 3.64082 8.78409 3.55876C8.78839 3.47671 8.77271 3.39597 8.73854 3.32431C8.70437 3.25265 8.65286 3.19249 8.58898 3.14961C8.5251 3.10672 8.45099 3.08257 8.37377 3.07946C8.29656 3.07635 8.21885 3.0944 8.14811 3.13186C8.07738 3.16932 8.016 3.22494 7.96988 3.29337C7.92376 3.3618 7.89445 3.44073 7.88478 3.52256C7.8751 3.6044 7.88539 3.6864 7.91464 3.76066L5.67125 9.68126C5.66122 9.70776 5.66135 9.73684 5.6716 9.76212C5.68186 9.78739 5.7014 9.80679 5.72593 9.81605L6.13841 9.97151C6.16295 9.98075 6.19047 9.97908 6.21492 9.96689C6.23937 9.95469 6.25875 9.93297 6.26881 9.90648L7.22068 7.39436C7.57587 7.37234 7.92777 7.42706 8.2549 7.55516C8.58203 7.68326 8.87752 7.88207 9.12332 8.13944C9.39591 8.42513 9.72525 8.64379 10.09 8.78126C10.4548 8.91873 10.8468 8.97194 11.2407 8.93745C11.26 8.93576 11.2788 8.92818 11.2947 8.91564C11.3105 8.90309 11.3229 8.88612 11.3302 8.86679L12.6128 5.48188C12.6191 5.46525 12.6215 5.44742 12.6197 5.43003C12.6179 5.41263 12.612 5.39622 12.6025 5.38228ZM8.0758 5.13776C8.51069 5.11069 8.93941 5.19875 9.32305 5.39395L8.97147 6.32184C8.58806 6.13002 8.16328 6.03691 7.73022 6.04977L8.0758 5.13776ZM9.49706 5.49294C9.67267 5.60133 9.83425 5.73232 9.97833 5.88312C10.1418 6.05435 10.3262 6.20185 10.5272 6.32211L10.1767 7.24703C10.0011 7.13864 9.83951 7.00765 9.69543 6.85685C9.53193 6.68562 9.34753 6.53812 9.1466 6.41786L9.49706 5.49294ZM10.7023 6.41813C10.7815 6.45771 10.8624 6.4936 10.9451 6.52477C11.2625 6.64443 11.601 6.70036 11.9439 6.68981L11.5982 7.60216C11.1633 7.62923 10.7346 7.54118 10.351 7.34598L10.7023 6.41813ZM12.3713 5.56164L12.0257 6.47382C11.5908 6.50094 11.1621 6.41291 10.7785 6.2177L11.1302 5.28942C11.5135 5.48136 11.9383 5.57452 12.3713 5.56164ZM10.9546 5.19406L10.6041 6.11897C10.4285 6.01061 10.2669 5.87961 10.1229 5.7288C9.95939 5.55753 9.775 5.40999 9.57408 5.28969L9.92467 4.36443C10.1003 4.47282 10.2619 4.60381 10.4059 4.75461C10.5694 4.92596 10.7537 5.07361 10.9546 5.19406ZM9.75064 4.2655L9.39901 5.1935C9.01561 5.00165 8.59083 4.90854 8.15776 4.92143L8.5034 4.00924C8.9383 3.9822 9.367 4.07028 9.75064 4.2655ZM8.42838 3.30612C8.4745 3.3235 8.514 3.35527 8.54187 3.39739C8.56973 3.43951 8.58472 3.4901 8.58494 3.54277C8.58515 3.59543 8.57059 3.6478 8.54308 3.69325C8.51557 3.73869 8.47635 3.77519 8.43039 3.7981C8.38443 3.82102 8.33378 3.82933 8.28486 3.82199C8.23595 3.81465 8.19095 3.79198 8.15556 3.75685C8.12017 3.72173 8.09598 3.67572 8.08606 3.62465C8.07613 3.57358 8.08091 3.51975 8.09979 3.46995C8.12515 3.40323 8.17399 3.34851 8.2356 3.31779C8.2972 3.28708 8.36653 3.28288 8.42838 3.30612ZM5.89415 9.65122L8.05473 3.94917C8.1202 4.00037 8.19905 4.03009 8.28212 4.03487L6.12154 9.73692L5.89415 9.65122ZM7.30258 7.1782L7.64824 6.26596C8.08314 6.2389 8.51185 6.32696 8.8955 6.52216L8.54394 7.44999C8.16049 7.25826 7.73564 7.16524 7.30258 7.1782ZM8.71898 7.54624L9.06953 6.62109C9.24516 6.72946 9.40674 6.86045 9.5508 7.01127C9.71429 7.1825 9.8987 7.33 10.0996 7.45026L9.74908 8.37541C9.57346 8.26704 9.41188 8.13604 9.26781 7.98523C9.10431 7.81403 8.91988 7.66652 8.71898 7.54624ZM9.92332 8.47377L10.2748 7.54622C10.354 7.5858 10.4348 7.62175 10.5175 7.65292C10.8349 7.77264 11.1734 7.82864 11.5163 7.81815L11.1702 8.73164C10.7353 8.75818 10.3067 8.66952 9.92332 8.47377Z" />
											</g>
											<defs>
												<clipPath id="clip0_516_22805">
													<rect
														width="11.1751"
														height="11.1751"
														fill="white"
														transform="translate(103.824)"
													/>
												</clipPath>
												<clipPath id="clip1_516_22805">
													<rect
														width="7.21349"
														height="7.76299"
														fill="white"
														transform="matrix(-0.991477 0.130281 0.176974 0.984216 7.15234 2.32715)"
													/>
												</clipPath>
												<clipPath id="clip2_516_22805">
													<rect
														width="7.11324"
														height="7.78889"
														fill="white"
														transform="matrix(0.951579 0.307405 -0.40432 0.914618 7.20117 2.31836)"
													/>
												</clipPath>
											</defs>
										</svg>

										<Inline gap="4">
											<Text style={{ fontSize: "5px" }}>
												Start: 21-May-2025
											</Text>
											<Text style={{ fontSize: "5px" }}>
												End: 25-May-2025 (4N/5D)
											</Text>
										</Inline>
									</Box>
									<Box
										paddingY={"1"}
										paddingX={"2"}
										style={{
											height: "44px",
											fontSize: "6px",
											fontWeight: "lighter",
										}}
									>
										<Text>Sales Person Title</Text>
										<Inline>
											<Icons.Photograph
												strokeWidth={1}
												style={{ width: "20px", height: "20px" }}
											/>
											<Box>
												<Text>Sale Person Name</Text>
												<Text>+91 876346788</Text>
											</Box>
										</Inline>
									</Box>
								</Inline>
							</Box>
						) : null}
					</Box>
					{preview ? (
						<Inline paddingX="4" paddingY={"2"} alignItems={"center"} gap="2">
							<Icons.Preview /> Preview
						</Inline>
					) : (
						<Inline padding="2" alignItems={"center"}>
							{joinAttributes(
								<Inline gap={"1"} alignItems={"center"}>
									<Icons.User /> {media?.created_by?.name || "You"}
								</Inline>,
								<Inline gap={"1"} alignItems={"center"}>
									<Icons.Calendar />{" "}
									<RelativeTime timestamp={media?.created_at} />
								</Inline>
							)}
						</Inline>
					)}
				</Box>
			) : (
				<Box
					style={{ width: "274px", height: "370px" }}
					display="flex"
					justifyContent="center"
					alignItems="center"
					bgColor="subtle"
					as="label"
					htmlFor="file"
				>
					<Stack>
						<Icons.Photograph
							style={{ width: "100px", height: "100px" }}
							color={"muted"}
						/>
						<Text color="muted" fontWeight="normal" fontSize="sm">
							No Image Selected
						</Text>
					</Stack>
				</Box>
			)}
		</Box>
	)
}
